import React, { useMemo, useRef } from 'react';
import { CnForm, CnButton, formilyCore, CnMessage, CnDialog } from '@sto/cn-ui';
import formRender from './formRender';
import Global from '@/utils/global';
import styles from './index.module.scss';
import { CustomTimePick, CustomCheckBox, SelectTimeGroup, ZcOrgSelect, CustomTree, FormFieldGroup,
  CustomRemoteSelect,
 } from '@/components';
import { request } from "@/api/request";
import mockData from './mockData.json';
import Answer from '$src/AIChart/components/chat/components/answer';
import { createSubscription, editSubscription } from '$src/services/fetchs'; // updateContent

const { createForm, onFormValuesChange } = formilyCore;

/**
 * 表单组件
 * @param content 表单内容
 * @param taskType 表单类型
 * @param askQuestion 询问问题
 * @param isHistory 是否是历史
 * @param disabled 是否禁用
 * @param formContentStyle 表单内容样式
 * @param submitBtnsStyle 提交按钮样式
 * @param initData 初始化数据(编辑时使用)
 * @param subscriptionId 订阅任务id(编辑时使用)
 * @param onSuccessCallback 成功回调
 * @param outputStatus 会话流输出状态
 * @param replaceSession 替换指定会话里的问题答案 
 * @returns 
 */
const CForm = ({ 
  content, 
  taskType, 
  askQuestion, 
  isHistory, 
  disabled = false,
  formContentStyle = {}, 
  submitBtnsStyle = {}, 
  initData = null,
  subscriptionId = null, // 订阅任务id(编辑时使用)
  onSuccessCallback = null, // 成功回调
  outputStatus = 'FINISHED', // 会话流输出状态
  replaceSession = null, // 替换指定会话里的问题答案
  requestId = null, // 问题id
}) => {
  // 获取组件: 当前项目的全局组件 + 其他引用Robot时传入的组件，比如：ZcOrgSelect是政策主应用传入的组织选择组件
  const components = useMemo(() => ({
    ...formRender,
    ...Global.components,
  }), []);

  const cForm = useMemo(
    () =>
      createForm({
        disabled: disabled,
        effects: () => {
          onFormValuesChange((form) => {
            const values = form.values;
            // console.log('表单值变化', values);
          })
        },
      }),
    [disabled],
  );

  // 按钮组的点击事件处理
  const onSubmit = async ({ type, url, extraInfo }: any) => {
    try {
      await cForm.validate();
      const values = cForm.values;
  
      // 订阅预览的接口入参结构
      const formData = {
        config: { // 表单的值+额外配置的参数
          ...values,
          ...extraInfo?.params,
        },
        requestId,
        platform: "WEB",
      }
  
      const res = await request({
        url, // 预览接口url
        method: 'POST',
        data: formData,
      });
      if (res.success) {
        const { data } = res;
        const { buttons } = data;
        const dialog = CnDialog.show({
          title: subscriptionId ? '编辑订阅任务' : '创建订阅任务',
          content: <div>
            {/* 展示预览内容 */}
            <Answer 
              isTestChat={false} 
              isHistory 
              {...data} 
              askQuestion={() => {}} 
              thoughtProgressFinished={true} 
              showGuess={false} 
              outputStatus={null} 
              contentListBoxStyle={{ boxShadow: 'none' }}
            />
          </div>,
          onOk: () => {
            const formatContent = typeof content === 'string' ? content : JSON.stringify(content);
            const createBody = {
              schema: formatContent,
              ...formData,
              subscriptionId,
              chatResp: data, // 预览内容
            }
  
            const fetchApi = subscriptionId ? editSubscription : createSubscription;
            fetchApi(createBody).then(response => {
              if (response.success) {
                dialog.hide();
                if (subscriptionId) {
                  onSuccessCallback?.();
                  CnMessage.success('编辑订阅任务成功');
                } else {
                  onSuccessCallback?.();
                  replaceSession?.(res.data);
                  CnMessage.success('创建订阅任务成功');
                  // updateContent(res.data);
                }              
              } else {
                CnMessage.error(response.errMessage);
              }
            });
          },
          width: '760px',
          okProps: {
            children: subscriptionId ? '编辑订阅任务' : '创建订阅任务',
          },
          footer: buttons ? <div style={{ float: 'left' }}>
            {buttons.map((item, index) => {
              const { url, method, params } = item.remoteConfig || {};
              return (
                <CnButton 
                  key={index} 
                  type="normal" 
                  onClick={() => {
                    if (url) {
                      request({ url, method, data: params, }).then(res => {
                        if (res.success) {
                          CnMessage.success(res.data);
                        } else {
                          CnMessage.error(res.errMessage);
                        }
                      });
                    } else {
                      CnMessage.error('按钮没有配置url');
                    }
                  }}
                >
                  {item.title}
                </CnButton>
              )
            })}
          </div> : null,
        });
      } else {
        CnMessage.error(res.errMessage);
      }
      
    } catch (error) {
      console.error(error, 'error');
    }
  }

  // 渲染表单
  const render = useMemo(() => {
    // const formatContent = mockData as any; // 测试数据
    const formatContent = typeof content === 'string' ? JSON.parse(content) : content;
    const { ZcOrgSelect: ZcOrgSelectComponent, ...restComponents } = (components as any) || {};
    const properties = formatContent?.properties || {};
    const keys = Object.keys(properties);
    // 提交按钮组
    const submitBtnKeys = keys.filter(key => key.includes('submit'));
    let submitBtns = [];
    if (submitBtnKeys?.length) {
      submitBtns = submitBtnKeys.map(key => properties[key]);
      submitBtnKeys.forEach(item => {
        delete properties[item];
      });
    }
    // 额外信息
    const extraInfo = properties?.extraInfo || {};
    if (initData) {
      // 编辑时，初始化表单值
      cForm.setValues({ ...initData });
    } else if (extraInfo?.params?.initialValues) {
      if (outputStatus === 'FINISHED') {
        // 会话输出完之后，再设置表单值
        // 创建时，初始化表单值（默认值）
        cForm.setValues(extraInfo.params.initialValues);
      }
      delete extraInfo.params.initialValues;
    }
    delete properties?.extraInfo;
    
    if (taskType && restComponents[taskType]) {
      const Content = restComponents[taskType];
      return (
        <Content
          isHistory={isHistory}
          content={formatContent}
          onSendQuestion={askQuestion}
          // receiver={receiver}
          // param={formatContent.param}
          // mainDt={mainDt}
          // status={formatContent.status || ''}
        />
      )
    }

    return <div className={styles.cFormBox} style={{ paddingBottom: submitBtns?.length ? '52px' : '0' }}>
      <div className={styles.cFormContent} style={formContentStyle}>
        <CnForm
          form={cForm}
          schema={{ ...formatContent }} 
          className={styles.cForm} 
          formLayoutProps={
            {
              labelCol: {
                fixedSpan: 4,
              },
            }
          }
          components={{
            CustomCheckBox,
            CustomTimePick,
            SelectTimeGroup,
            // ZcOrgSelect,
            ZcOrgSelect: ZcOrgSelectComponent,
            CustomTree,
            FormFieldGroup,
            CustomRemoteSelect,
            ...restComponents, // 其他自定义组件
          }}
        />
      </div>
      {/* 表单按钮组 */}
      {submitBtns?.length ? <div className={styles.submitBtns} style={submitBtnsStyle}>
        {submitBtns.map((item, index) => {
          const itemProps = item["x-component-props"] || {};
          const { type, url, ...restProps } = itemProps;
          if (url === '/subscription/create') {
            // PC端不展示创建订阅任务按钮
            return null;
          }
          return (
            <CnButton 
              key={index} 
              {...restProps} 
              type="primary" 
              onClick={() => onSubmit({ type, url, extraInfo })} 
              disabled={disabled}
            />
          );
        })}
      </div> : null}
    </div>
  }, [askQuestion, content, isHistory, taskType, components, outputStatus]);

  return render;
}

export default CForm;
